import MarkdownRenderer from "@/components/UI/MarkdownRenderer";
import type { InsightReportPayload } from "./TrialNewsReport";
import { parseTrialOverview, type OverviewSection } from "./trialOverview";

const TONE_STYLE: Record<
  OverviewSection["tone"],
  { border: string; heading: string }
> = {
  neutral: { border: "border-[#E5EAEC]", heading: "text-[#556C75]" },
  positive: {
    border: "border-l-4 border-[#004A42]",
    heading: "text-[#004A42]",
  },
  attention: {
    border: "border-l-4 border-[#B45309]",
    heading: "text-[#B45309]",
  },
};

const SectionCard = ({ section }: { section: OverviewSection }) => {
  const style = TONE_STYLE[section.tone];
  return (
    <div className={`rounded-md border bg-white p-4 ${style.border}`}>
      <p
        className={`mb-2 text-[12px] font-bold uppercase tracking-wide ${style.heading}`}
      >
        {section.heading}
      </p>
      <div className="markdown text-[13px] leading-relaxed text-[#2B4752]">
        <MarkdownRenderer content={section.body} />
      </div>
    </div>
  );
};

export default function TrialOverviewReport({
  report,
  fallbackMarkdown,
}: {
  report: InsightReportPayload | null | undefined;
  fallbackMarkdown: string;
}) {
  const summary = report?.summary?.trim();
  if (!summary) {
    return (
      <div className="markdown">
        <MarkdownRenderer content={fallbackMarkdown} />
      </div>
    );
  }

  const parsed = parseTrialOverview(summary);
  if (!parsed) {
    return (
      <div className="markdown text-[13px] leading-relaxed text-[#2B4752]">
        <MarkdownRenderer content={summary} />
      </div>
    );
  }

  // parsed.keyFigures is deliberately not rendered: the stats duplicated what
  // the Current state section already says in prose, and the chip row pushed the
  // narrative below the fold. parseTrialOverview still consumes the
  // "**Key figures:**" line so it does not fall through into the intro.
  const accentSections = parsed.sections.filter(
    (section) => section.tone !== "neutral",
  );
  const proseSections = parsed.sections.filter(
    (section) => section.tone === "neutral",
  );

  return (
    <div className="space-y-4">
      {parsed.intro && (
        <div className="markdown text-[14px] leading-relaxed text-[#2B4752]">
          <MarkdownRenderer content={parsed.intro} />
        </div>
      )}
      {proseSections.map((section) => (
        <SectionCard key={section.heading} section={section} />
      ))}
      {accentSections.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {accentSections.map((section) => (
            <SectionCard key={section.heading} section={section} />
          ))}
        </div>
      )}
    </div>
  );
}
